
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SquarePaymentForm from '../components/SquarePaymentForm';
import { useSubscriptionSelection } from '../context/SubscriptionContext';
import { motion } from 'framer-motion';

const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APP_ID || '';
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID || '';

const Subscribe: React.FC = () => {
  const navigate = useNavigate();
  const TAX_RATE = 0.0675;
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [planId, setPlanId] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { selection, setSelection } = useSubscriptionSelection();
  const availableTerms = (selection?.availableTerms ?? []) as string[];

  // Initialize from selection passed in, do not fetch from API
  React.useEffect(() => {
    // If no product context, redirect back to subscriptions to choose one
    if (!selection?.productId || !selection?.productName) {
      navigate('/subscriptions');
      return;
    }
    if (selection?.planId) {
      setPlanId(selection.planId);
    }
    if (selection?.interval) {
      // keep as-is
    }
  }, [selection, navigate]);

  // When interval changes, ensure planId and price reflect selected term
  React.useEffect(() => {
    if (selection && selection.interval && selection.optionsByTerm) {
      const opt = selection.optionsByTerm[selection.interval];
      if (opt && (selection.planId !== opt.planVariationId || selection.price !== opt.price || selection.productId !== opt.productId)) {
        setSelection({
          ...selection,
          planId: opt.planVariationId,
          price: opt.price,
          locationId: opt.locationId ?? selection.locationId,
          productId: opt.productId ?? selection.productId
        });
        setPlanId(opt.planVariationId);
      }
    }
  }, [selection, selection?.interval, setSelection]);

  const requestTokenRef = useRef<(() => Promise<string | null>) | null>(null);

  const handlePay = async () => {
    setLoading(true);
    setMessage('');
    if (!requestTokenRef.current) {
      setMessage('Card form not ready.');
      setLoading(false);
      return;
    }
    const token = await requestTokenRef.current();
    if (!token) {
      setLoading(false);
      return;
    }
    const payload = {
      customerName,
      customerEmail,
      paymentToken: token,
      planVariationId: planId,
      locationId: selection?.locationId,
      productId: selection?.productId // term-specific productId from optionsByTerm
    };
    console.log('POST payload:', payload);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/subscriptions/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const receipt = await res.json();
        setMessage('Subscription created!');
        const subtotal = selection?.price || 0;
        const tax = subtotal * TAX_RATE;
        const total = subtotal + tax;
        navigate('/receipt', {
          state: {
            receipt: {
              items: [
                {
                  name: selection?.productName || '',
                  quantity: 1,
                  price: selection?.price || 0
                }
              ],
              subtotal,
              tax,
              total,
              customerName,
              customerEmail,
              transactionId: receipt?.transactionId,
              date: receipt?.date
            }
          }
        });
      } else {
        setMessage('Subscription failed.');
      }
    } catch {
      setMessage('Subscription error.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FDF8F5] py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif text-4xl font-semibold text-[#3D3D3D] mb-8">Subscribe</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Summary */}
          <div className="lg:col-span-2 space-y-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between flex-col sm:flex-row">
                <div className="min-w-0">
                  <h3 className="font-serif text-lg font-semibold text-[#3D3D3D] mb-1 truncate">{selection?.productName || 'Selected Product'}</h3>
                  <p className="font-sans text-sm text-[#3D3D3D]/60">Billed: {selection?.interval || 'Term'}</p>
                </div>
                <div className="text-right self-end">
                  <p className="font-serif text-xl font-semibold text-[#3D3D3D]">${(selection?.price ?? 0).toFixed(2)}</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Payment Details */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl p-8 shadow-lg sticky top-32">
              <h2 className="font-serif text-2xl font-semibold text-[#3D3D3D] mb-6">Payment Details</h2>

              <div className="space-y-3 mb-6 pb-6 border-b border-gray-100">
                <div className="flex justify-between font-sans text-[#3D3D3D]">
                  <span>Subtotal (1 item)</span>
                  <span>${(selection?.price ?? 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-sans text-[#3D3D3D]">
                  <span>Tax (6.75%)</span>
                  <span>${(((selection?.price ?? 0) * TAX_RATE)).toFixed(2)}</span>
                </div>
              </div>
              <div className="flex justify-between font-serif text-2xl font-semibold text-[#3D3D3D] mb-6">
                <span>Total</span>
                <span>${(((selection?.price ?? 0) * (1 + TAX_RATE))).toFixed(2)}</span>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="font-sans text-sm text-[#3D3D3D] mb-2 block">Your Name</label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    required
                    placeholder="Jane Doe"
                    className="py-6 rounded-xl border border-gray-200 font-sans w-full"
                  />
                </div>
                <div>
                  <label className="font-sans text-sm text-[#3D3D3D] mb-2 block">Your Email</label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    required
                    placeholder="jane@example.com"
                    className="py-6 rounded-xl border border-gray-200 font-sans w-full"
                  />
                </div>
                <div>
                  <label htmlFor="term-select" className="font-sans text-sm text-[#3D3D3D] mb-2 block">Subscription Term</label>
                  <select
                    id="term-select"
                    value={selection?.interval || (availableTerms.length ? availableTerms[0] : '')}
                    onChange={e => {
                      const newInterval = e.target.value;
                      const opt = selection?.optionsByTerm?.[newInterval];
                      setSelection({
                        ...selection!,
                        interval: newInterval,
                        planId: opt?.planVariationId || planId,
                        price: opt?.price ?? selection?.price,
                        locationId: opt?.locationId ?? selection?.locationId
                      });
                      if (opt?.planVariationId) setPlanId(opt.planVariationId);
                    }}
                    required
                    className="py-3 rounded-xl border border-gray-200 font-sans w-full"
                  >
                    {(availableTerms.length ? availableTerms : ['Monthly','Annual']).map(term => (
                      <option key={term} value={term}>{term}</option>
                    ))}
                  </select>
                </div>

                <SquarePaymentForm
                  appId={SQUARE_APP_ID}
                  locationId={SQUARE_LOCATION_ID}
                  requestTokenRef={requestTokenRef}
                  onToken={(token: string): void => {}}
                  className="space-y-2"
                />

                <button
                  className="w-full bg-[#3D3D3D] hover:bg-[#2D2D2D] text-white py-3 rounded-full font-sans text-sm leading-none"
                  disabled={!customerName.trim() || !customerEmail.trim() || !planId.trim() || loading}
                  onClick={handlePay}
                >
                  {loading ? 'Processing...' : 'Pay Now'}
                </button>
                {message && <div className="text-[#3D3D3D] font-sans mt-3">{message}</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Subscribe;
